import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CandidateCard from './CandidateCard';

interface CandidateResult {
  cv_id: string;
  score: number;
  keywords: string[];
  preview: string;
  // CV Metadata (optional)
  career_status?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
  cv_language?: string; // "en" or "de" - from Pinecone metadata
  reach_out_status?: string;
  reach_out_id?: string;
  conversation_id?: string;
}

interface LanguageFilter {
  de: boolean;
  en: boolean;
}

interface CVKeywords {
  cv_id: string;
  keywords: string[];
  sections: Record<string, string[]>;
}

interface CandidateResultsListProps {
  results: CandidateResult[];
  selectedCV: string | null;
  onCVSelect: (cvId: string) => void;
  cvKeywords: Record<string, CVKeywords>;
  isLoading: boolean;
  languageFilter: LanguageFilter;
  onLanguageFilterChange: (filter: LanguageFilter) => void;
  hasSearched: boolean; // Whether a search has been performed
  showSave?: boolean;
  savedCvIds?: Set<string>;
  onToggleSave?: (cvId: string, e: React.MouseEvent) => void;
  skipSaved?: boolean;
  onSkipSavedChange?: (v: boolean) => void;
}

const CandidateResultsList: React.FC<CandidateResultsListProps> = ({ 
  results, 
  selectedCV, 
  onCVSelect, 
  cvKeywords, 
  isLoading,
  languageFilter,
  onLanguageFilterChange,
  hasSearched,
  showSave = false,
  savedCvIds,
  onToggleSave,
  skipSaved = false,
  onSkipSavedChange
}) => {
  const { t } = useTranslation('hire');

  if (isLoading) {
    return (
      <div className="p-3 sm:p-6">
        <div className="space-y-2 sm:space-y-3">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.3 }}
              className="relative bg-white/95 rounded-xl p-3 sm:p-4 border-2 border-slate-200 shadow-sm overflow-hidden"
            >
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/40 to-transparent z-10" />
              
              {/* Bookmark top-right */}
              <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 w-5 h-5 sm:w-6 sm:h-6 bg-slate-100 rounded-lg" />

              {/* Candidate info lines */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 rounded bg-emerald-100" />
                  <div className="h-3 bg-slate-100 rounded w-24 sm:w-32" />
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 rounded bg-blue-100" />
                  <div className="h-3 bg-slate-100 rounded w-28 sm:w-40" />
                </div>
                {i % 2 === 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 rounded bg-purple-100" />
                    <div className="h-3 bg-slate-100 rounded w-20 sm:w-36" />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="p-3 sm:p-6 h-full flex flex-col">
        {/* Show filter header even when no results (if search was performed) */}
        {hasSearched && (
          <motion.div 
            className="mb-4 sm:mb-6 flex items-center justify-between gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base sm:text-xl font-semibold text-slate-800 truncate min-w-0">
              👥 {t('results.candidatesCount')} (0)
            </h2>
            
            {/* Language Filter (inline text style) */}
            <div className="flex items-center gap-0.5 text-[10px] text-slate-400">
              <button
                onClick={() => onLanguageFilterChange({ de: true, en: true })}
                className={`transition-colors ${
                  languageFilter.de && languageFilter.en ? 'text-slate-700 font-medium' : 'hover:text-slate-500'
                }`}
              >
                {t('results.all', 'Alle')}
              </button>
              <span className="mx-0.5">·</span>
              <button
                onClick={() => onLanguageFilterChange({ de: true, en: false })}
                className={`transition-colors ${
                  languageFilter.de && !languageFilter.en ? 'text-slate-700 font-medium' : 'hover:text-slate-500'
                }`}
              >
                DE
              </button>
              <span className="mx-0.5">·</span>
              <button
                onClick={() => onLanguageFilterChange({ de: false, en: true })}
                className={`transition-colors ${
                  !languageFilter.de && languageFilter.en ? 'text-slate-700 font-medium' : 'hover:text-slate-500'
                }`}
              >
                EN
              </button>
            </div>
          </motion.div>
        )}
        
        <motion.div 
          className="flex-1 flex items-center justify-center text-slate-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="text-center">
            <div className="flex justify-center mb-1.5 sm:mb-2">
              <span className="text-2xl sm:text-3xl">👥</span>
            </div>
            <p className="text-xs sm:text-sm">{t('results.noResults')}</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Results Header with Language Filter */}
      <motion.div 
        className="mb-4 sm:mb-6 flex items-center justify-between gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-base sm:text-xl font-semibold text-slate-800 truncate min-w-0">
          👥 {t('results.candidatesCount')} ({results.length})
        </h2>
        
        {/* Language Filter + Skip Saved (inline text style) */}
        <div className="flex items-center gap-2 text-[10px] text-slate-400">
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => onLanguageFilterChange({ de: true, en: true })}
              className={`transition-colors ${
                languageFilter.de && languageFilter.en ? 'text-slate-700 font-medium' : 'hover:text-slate-500'
              }`}
            >
              {t('results.all', 'Alle')}
            </button>
            <span className="mx-0.5">·</span>
            <button
              onClick={() => onLanguageFilterChange({ de: true, en: false })}
              className={`transition-colors ${
                languageFilter.de && !languageFilter.en ? 'text-slate-700 font-medium' : 'hover:text-slate-500'
              }`}
            >
              DE
            </button>
            <span className="mx-0.5">·</span>
            <button
              onClick={() => onLanguageFilterChange({ de: false, en: true })}
              className={`transition-colors ${
                !languageFilter.de && languageFilter.en ? 'text-slate-700 font-medium' : 'hover:text-slate-500'
              }`}
            >
              EN
            </button>
          </div>
          {onSkipSavedChange && savedCvIds && savedCvIds.size > 0 && (
            <>
              <span className="text-slate-300">|</span>
              <button
                onClick={() => onSkipSavedChange(!skipSaved)}
                className={`flex items-center gap-0.5 transition-colors ${
                  skipSaved ? 'text-amber-600 font-medium' : 'hover:text-slate-500'
                }`}
              >
                <EyeOff className="w-2.5 h-2.5" />
                {t('results.saved', 'Saved')}
              </button>
            </>
          )}
        </div>
      </motion.div>

      {/* Results List */}
      <div className="space-y-2 sm:space-y-3">
        <AnimatePresence>
          {results.map((result, index) => {
            const keywords = cvKeywords[result.cv_id]?.keywords || result.keywords || [];
            
            return (
              <motion.div
                key={result.cv_id}
                initial={{ 
                  opacity: 0, 
                  y: 30, 
                  scale: 0.95
                }}
                animate={{ 
                  opacity: 1, 
                  y: 0, 
                  scale: 1
                }}
                transition={{
                  duration: 0.25,
                  ease: "easeOut",
                  delay: index * 0.05
                }}
              >
                <CandidateCard
                  result={result}
                  keywords={keywords}
                  isSelected={selectedCV === result.cv_id}
                  onClick={() => onCVSelect(result.cv_id)}
                  index={index}
                  isSaved={showSave && savedCvIds?.has(result.cv_id)}
                  onToggleSave={showSave && onToggleSave ? (e) => onToggleSave(result.cv_id, e) : undefined}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* No more results indicator */}
      {results.length > 0 && (
        <motion.div 
          className="text-center py-4 text-slate-400 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {t('results.endOfResults')}
        </motion.div>
      )}
    </div>
  );
};

export default CandidateResultsList;
